import { ISillyLogger } from './logger';

interface IProfilePromiseArgs<T> {
  method: string,
  uri: string,
  promise: Promise<T>,
  logger?: ISillyLogger,
  shouldLog?: boolean,
  overridePerformance?: IPerformance,
  overrideProcess?: IProcess
}

interface IPerformance {
  now: () => number
}

interface IProcess {
  hrtime: (args?: [number, number]) => [number, number]
}

declare const performance: IPerformance;

const enum Colors {
  cyan = '\u001b[36m',
  magenta = '\u001b[35m',
  blue = '\u001b[34m',
  reset = '\u001b[0m',
}

export async function profilePromise<T>(
  {
    method,
    uri,
    promise,
    logger,
    shouldLog,
    overridePerformance = typeof performance !== 'undefined' && performance,
    overrideProcess = process,
  }: IProfilePromiseArgs<T>,
): Promise<T> {
  const start = shouldLog && logger && calculateStart(overridePerformance, overrideProcess);
  const response = await promise;
  if (shouldLog && logger) {
    const elapsedMS = elapsedMilliseconds(start, overridePerformance, overrideProcess);
    const retVal = formatTime(elapsedMS) + ' ' + calculateLabel(method, uri);
    logger.silly(retVal);
  }
  return response;
}

function calculateStart(overridePerformance: IPerformance, overrideProcess: IProcess): number | [number, number] {
  // Browsers:
  if (typeof overridePerformance !== 'undefined' && overridePerformance?.now) {
    return overridePerformance.now();
  }
  // NodeJS:
  if (typeof overrideProcess !== 'undefined' && overrideProcess?.hrtime) {
    return overrideProcess.hrtime();
  }
  // Fallback:
  return Date.now();
}

function elapsedMilliseconds(start: number | [number, number], overridePerformance: IPerformance, overrideProcess: IProcess) {
  // Browsers:
  if (typeof start === 'number' && typeof overridePerformance !== 'undefined' && overridePerformance?.now) {
    return overridePerformance.now() - start;
  }
  // NodeJS:
  if (typeof start !== 'number' && typeof overrideProcess !== 'undefined' && overrideProcess?.hrtime) {
    const hrTime = overrideProcess.hrtime(start);
    const oneThousand = 1e3;
    const oneMillion = 1e6;
    return hrTime[0] * oneThousand + hrTime[1] / oneMillion;
  }
  // Fallback:
  return Date.now() - (typeof start !== 'number' ? start[0] : start);
}

function formatTime(elapsedMS: number): string {
  const fractionalDigits = 2;
  const timeColumnWidth = 9;
  let retVal = elapsedMS.toFixed(fractionalDigits);
  while (retVal.length < timeColumnWidth) {
    retVal = ' ' + retVal;
  }
  return retVal + 'ms';
}

function calculateLabel(method: string, uri: string): string {
  return `${ Colors.magenta }${ method } ${ Colors.cyan }${ uri }${ Colors.reset }`;
}
